import { apiClient } from '../../../services/api';
import { apiClientBff } from '../../../services/apiBff';
import { 
  UserProfile, 
  UpdateProfileRequest,
  InsertProfileRequest,
  SearchProfileRequest,
  UpdateProfileAQLRequest,
  DeleteProfileRequest,
  AggregateProfileRequest,
  ServiceResponse
} from '../types/profileTypes';

class ProfileService {
  // Use collection path without duplicating base /api/v2
  private readonly endpoint = '/UserProfile';

  /**
   * Insert a new user profile
   */
  async insert(request: InsertProfileRequest): Promise<ServiceResponse<UserProfile>> {
    try {
      const profileData = {
        ...request.data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Route via BFF proxy to avoid CORS and point to dev API
      const response = await apiClientBff.post(`/profile/insert`, {
        data: profileData
      });
      
      return {
        success: true,
        data: response as UserProfile,
        message: 'Profile created successfully'
      };
    } catch (error: any) {
      console.error('Error inserting profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to create profile',
        message: 'Profile creation failed'
      };
    }
  }

  /**
   * Search for user profiles using AQL queries
   */
  async search(request: SearchProfileRequest): Promise<ServiceResponse<UserProfile[]>> {
    try {
      // Route via BFF proxy to avoid CORS and point to dev API
      const response = await apiClientBff.post(`/profile/search`, request);
      
      // Check if response has nested data structure
      let profileData = response;
      if (response && typeof response === 'object') {
        const responseObj = response as any;
        if (responseObj.data) {
          profileData = responseObj.data;
        }
        if (responseObj.result) {
          profileData = responseObj.result;
        }
      }
      
      return {
        success: true,
        data: profileData as UserProfile[],
        message: 'Search completed successfully'
      };
    } catch (error: any) {
      console.error('Error searching profiles:', error);
      return {
        success: false,
        error: error.message || 'Search failed',
        message: 'Profile search failed'
      };
    }
  }

  /**
   * Update user profiles using AQL queries
   */
  async update(request: UpdateProfileAQLRequest): Promise<ServiceResponse<UserProfile>> {
    try {
      const response = await apiClientBff.post(`/profile/update`, request);
      return {
        success: true,
        data: response as UserProfile,
        message: 'Profile updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to update profile',
        message: 'Profile update failed'
      };
    }
  }

  /**
   * Delete user profiles using AQL queries
   */
  async delete(request: DeleteProfileRequest): Promise<ServiceResponse<any>> {
    try {
      const response = await apiClientBff.post(`/profile/delete`, request);
      return {
        success: true,
        data: response,
        message: 'Profile deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete profile',
        message: 'Profile deletion failed'
      };
    }
  }

  /**
   * Aggregate user profile data using AQL queries
   */
  async aggregate(request: AggregateProfileRequest): Promise<ServiceResponse<any>> {
    try {
      const response = await apiClientBff.post(`/profile/aggregate`, request);
      return {
        success: true,
        data: response,
        message: 'Aggregation completed successfully'
      };
    } catch (error: any) {
      console.error('Error aggregating profiles:', error);
      return {
        success: false,
        error: error.message || 'Aggregation failed',
        message: 'Profile aggregation failed'
      };
    }
  }

  // Convenience methods for common operations

  /**
   * Get a profile by username
   */
  async getProfileByUsername(username: string): Promise<ServiceResponse<UserProfile>> {
    const searchRequest: SearchProfileRequest = {
      bind_vars: {
        '@collection': 'UserProfile',
        username: username
      },
      query: 'FOR doc IN @@collection FILTER doc.username == @username RETURN doc'
    };

    const result = await this.search(searchRequest);
    
    if (result.success && result.data && result.data.length > 0) {
      return {
        success: true,
        data: result.data[0],
        message: 'Profile found successfully'
      };
    }

    return {
      success: false,
      error: 'Profile not found',
      message: 'No profile found with the given username'
    };
  }

  /**
   * Update a profile by username
   */
  async updateProfileByUsername(username: string, updates: UpdateProfileRequest): Promise<ServiceResponse<UserProfile>> {
    const updateRequest: UpdateProfileAQLRequest = {
      bind_vars: {
        '@collection': 'UserProfile',
        username: username,
        updates: {
          ...updates,
          updated_at: new Date().toISOString()
        }
      },
      query: 'FOR doc IN @@collection FILTER doc.username == @username UPDATE doc WITH @updates IN @@collection RETURN NEW'
    };

    return this.update(updateRequest);
  }

  /**
   * Delete a profile by username
   */
  async deleteProfileByUsername(username: string): Promise<ServiceResponse<any>> {
    const deleteRequest: DeleteProfileRequest = {
      bind_vars: {
        '@collection': 'UserProfile',
        username: username
      },
      query: 'FOR doc IN @@collection FILTER doc.username == @username REMOVE doc IN @@collection RETURN OLD'
    };

    return this.delete(deleteRequest);
  }

  /**
   * Search profiles by profile type
   */
  async getProfilesByType(profileType: string): Promise<ServiceResponse<UserProfile[]>> {
    const searchRequest: SearchProfileRequest = {
      bind_vars: {
        '@collection': 'UserProfile',
        profile_type: profileType
      },
      query: 'FOR doc IN @@collection FILTER doc.profile_type == @profile_type RETURN doc'
    };

    return this.search(searchRequest);
  }

  /**
   * Get profiles by farm association
   */
  async getProfilesByFarmId(farmId: string): Promise<ServiceResponse<UserProfile[]>> {
    const searchRequest: SearchProfileRequest = {
      bind_vars: {
        '@collection': 'UserProfile',
        farm_id: farmId
      },
      query: 'FOR doc IN @@collection FILTER @farm_id IN doc.farm_associations[*].farm_id RETURN doc'
    };

    return this.search(searchRequest);
  }
}

export const profileService = new ProfileService();
export default profileService;